import { usersId, type UsersId } from "./Users";
import { organizationsId, type OrganizationsId } from "./Organizations";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for api_keys */
export type ApiKeysId = string & { __flavor: "ApiKeysId" };

/**
 * Represents the table public.api_keys
 * @uniqueConstraints {"api_keys_pkey":{"name":"api_keys_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"unique_key":{"name":"unique_key","isPrimary":false,"isUnique":true,"columns":[{"name":"key","definition":"key","predicate":null}]},"idx_created_at":{"name":"idx_created_at","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]},"idx_updated_at":{"name":"idx_updated_at","isPrimary":false,"isUnique":false,"columns":[{"name":"updated_at","definition":"updated_at","predicate":null}]},"unique_name_org":{"name":"unique_name_org","isPrimary":false,"isUnique":true,"columns":[{"name":"name","definition":"name","predicate":null},{"name":"organization_id","definition":"organization_id","predicate":null}]}}
 */
export default interface ApiKeysTable {
  id: ColumnType<ApiKeysId, ApiKeysId | undefined, ApiKeysId>;

  name: ColumnType<string, string, string>;

  key: ColumnType<string, string, string>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>;

  creatorId: ColumnType<UsersId, UsersId, UsersId>;

  organizationId: ColumnType<OrganizationsId, OrganizationsId, OrganizationsId>;

  forWholeOrg: ColumnType<boolean, boolean | undefined, boolean>;
}

export type ApiKeys = Selectable<ApiKeysTable>;

export type NewApiKeys = Insertable<ApiKeysTable>;

export type ApiKeysUpdate = Updateable<ApiKeysTable>;

export const apiKeysId = z.string() as unknown as z.Schema<ApiKeysId>;

export const apiKeys = z.object({
  id: apiKeysId,
  name: z.string(),
  key: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  creatorId: usersId,
  organizationId: organizationsId,
  forWholeOrg: z.boolean(),
});

export const apiKeysInitializer = z.object({
  id: apiKeysId.optional(),
  name: z.string(),
  key: z.string(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  creatorId: usersId,
  organizationId: organizationsId,
  forWholeOrg: z.boolean().optional(),
});

export const apiKeysMutator = z.object({
  id: apiKeysId.optional(),
  name: z.string().optional(),
  key: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  creatorId: usersId.optional(),
  organizationId: organizationsId.optional(),
  forWholeOrg: z.boolean().optional(),
});

// ── constraints for ApiKeys ─────────────────────────────────
export const ApiKeysConstraints = {
  api_keys_pkey: {
    name: "api_keys_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  unique_key: {
    name: "unique_key",
    isPrimary: false,
    isUnique: true,
    columns: [{ name: "key", definition: "key", predicate: null }],
  },
  idx_created_at: {
    name: "idx_created_at",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
  idx_updated_at: {
    name: "idx_updated_at",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "updated_at", definition: "updated_at", predicate: null }],
  },
  unique_name_org: {
    name: "unique_name_org",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "name", definition: "name", predicate: null },
      { name: "organization_id", definition: "organization_id", predicate: null },
    ],
  },
} as const;
export type ApiKeysConstraintsType = typeof ApiKeysConstraints;
